import { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { PaginationI } from '~src/interfaces/pagination.interface';
import { SegmentType } from '~src/interfaces/segment.interface';
import { SessionUserI } from '~src/interfaces/session.types';
import { getUserFullName, User } from '~src/interfaces/user.interface';
import { SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { fetchUser, FetchUserParams, fetchUsers } from '~src/modules/users/actions/user.actions';
import { UsersRespI } from '~src/modules/users/interfaces';
import { getUsers } from '~src/modules/users/reducers/userManagementReducer';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp, isSuccess } from '~src/services/apiMiddleware';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';

import SearchListSelector from '~src/shared/selectors/searchListSelector/SearchListSelector';
import { MSFilterTypes, ObjectivesQueryI, ObjectiveStatus } from '~src/store/okrs/interfaces';

type OwnersFilterI = Pick<ObjectivesQueryI, MSFilterTypes.Owner>;

interface StateProps {
  users: User[] | null;
  pagination: PaginationI | null;
  loadingUsers: boolean;
  sessionUser: SessionUserI | null;
}

interface DispatchProps {
  fetchUsers(params: FetchUserParams, loadMore?: boolean): ApiReduxResp<UsersRespI>;
  fetchUser(uuid: string): ApiReduxResp<User>;
}

interface OwnProps {
  initialOpts?: OwnersFilterI[MSFilterTypes.Owner];
  applyFilter(filterName: MSFilterTypes, ids: OwnersFilterI[MSFilterTypes.Owner]): void;
}

type Props = OwnProps & StateProps & DispatchProps;

const OwnersSelector: FC<Props> = (props) => {
  const initialOpts = useMemo<string[] | undefined>(() => {
    if (props.initialOpts && props.sessionUser) {
      const initialOptCopy = [...props.initialOpts];
      const sessionUserIdIndex = initialOptCopy.indexOf(props.sessionUser.uuid);
      if (sessionUserIdIndex !== -1) {
        initialOptCopy.splice(sessionUserIdIndex, 1);
      }
      return initialOptCopy;
    }
    return undefined;
  }, [props.initialOpts]);
  const [selectorInactive, setSelectorInactive] = useState<boolean>(!initialOpts || !initialOpts.length);

  useEffect(() => {
    void fetchUsersW();
  }, []);

  const optionsList = useMemo(() => {
    if (!props.users || !props.sessionUser) return [];

    return props.users.reduce<OptionItem[]>((acc, user) => {
      if (user.uuid !== props.sessionUser!.uuid) {
        acc.push({
          id: user.uuid,
          name: getUserFullName(user),
          avatarUrl: user.imageUrl,
          avatarType: SegmentType.User,
        });
      }
      return acc;
    }, []);
  }, [props.users, props.sessionUser]);

  const getInitiallySelectedOptionName = async (id: string) => {
    const action = await props.fetchUser(id);
    if (isSuccess(action)) {
      const { response: {data } } = action;
      return getUserFullName(data);
    }

    return '';
  };

  const fetchUsersW = (params?: string, loadMore?: boolean): ApiReduxResp<UsersRespI> => {
    let queryObj: FetchUserParams = { sort: SortingValues.Firstname_asc, per_page: 10 };

    if (params) {
      queryObj = { ...queryObj, search: params };
    }

    if (loadMore && props.pagination) {
      queryObj.page = props.pagination.next_page;
    }

    return props.fetchUsers(queryObj, loadMore);
  };

  const applyChecked = (ids: string[] | OptionItem) => {
    if (!Array.isArray(ids)) {
      return;
    }
    props.applyFilter(MSFilterTypes.Owner, ids as ObjectiveStatus[]);
    setSelectorInactive(!ids.length);
  };

  const resetSelected = () => {
    props.applyFilter(MSFilterTypes.Owner, undefined);
    setSelectorInactive(true);
  };

  return (
    <SearchListSelector
      multiSelect
      initialOpt={initialOpts}
      inactive={selectorInactive}
      inactiveKey="x.okr.objectives.users_selector.inactive_label"
      onSelect={applyChecked}
      optionsList={optionsList}
      loadingSearch={props.loadingUsers}
      fetchItems={fetchUsersW}
      noMoreItems={!!(props.pagination && !props.pagination.next_page)}
      nothingFoundKey="x.okr.objectives.users_selector.no_users_found"
      resetSelected={resetSelected}
      getInitiallySelectedOptionName={getInitiallySelectedOptionName}
    />
  );
};

const mapState2Props = (state: StoreI): StateProps => ({
  users: getUsers(state),
  pagination: state.userManagement.pagination,
  loadingUsers: state.userManagement.loading,
  sessionUser: state.user.session.user,
});

const mapDispatch2Props = (dispatch: any): DispatchProps => ({
  fetchUsers: (params, loadMore) => dispatch(fetchUsers(params, loadMore)),
  fetchUser: (id) => dispatch(fetchUser(id)),
});

export default connect(mapState2Props, mapDispatch2Props)(OwnersSelector);
